import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ApiClientResponseError,
  type ResponseErrorDataObject,
  isValidationResponseErrorDataObject,
} from 'common/services';

export const useGetErrorMessage = () => {
  const { t } = useTranslation();

  return useCallback(
    (error: ApiClientResponseError<ResponseErrorDataObject>) => {
      const errorData = error.response?.data;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      if (isValidationResponseErrorDataObject(errorData) && errorData.errors) {
        return {
          errorMessage: Object.entries(errorData.errors)
            .flatMap(([key, value]) => value.map((reason: string) => `${key} ${reason}`))
            .join(', '),
        };
      }

      if (error.response?.data['invalid-params']) {
        const invalidParams = error.response.data['invalid-params'];
        const duration = 5;

        if (
          invalidParams.some((invalid: { name: string; reason: string }) => invalid.name === 'reset_password_token')
        ) {
          return { duration, errorMessage: t('reset_password_token is invalid') };
        }

        if (
          invalidParams.some((invalid: { name: string; reason: string }) => invalid.reason === 'was used previously.')
        ) {
          return { duration, errorMessage: t('passwordErrorUsed') };
        }

        if (
          invalidParams.some(
            (invalid: { name: string; reason: string }) =>
              invalid.name === 'current_password' && invalid.reason === 'is invalid',
          )
        ) {
          return { duration, errorMessage: t('passwordErrorCurrentPasswordInvalid') };
        }

        return { duration, errorMessage: t('passwordError') };
      }

      return { errorMessage: t('Something went wrong. Password cannot be changed right now.') };
    },
    [t],
  );
};
